import axios from "axios";
import useAuthStore from "@/zustand/authStore";
import { getSessionToken } from "@/services/auth/clerk";
import { S3_ENDPOINT } from "@/types/constants/common";

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BASE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const supplierContentAxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_SUPPLIER_CONTENT_BASE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// interceptors
// Request interceptor
const AUTH_ERROR_MESSAGES = ["No token provided", "Failed to authenticate token"];

// Several requests fire at once on page load; only the first auth failure should log out.
let loggingOut = false;
const logoutOnce = () => {
  if (loggingOut) return;
  loggingOut = true;
  useAuthStore.getState().logoutUser();
};

// Only our own APIs get the session token. Presigned S3 URLs carry their own signature,
// and S3 rejects a request with both that and an Authorization header (400 InvalidArgument).
const TRUSTED_ORIGINS = new Set(
  [
    import.meta.env.VITE_BASE_API_URL,
    import.meta.env.VITE_BASE_API_URL_V2,
    import.meta.env.VITE_SUPPLIER_CONTENT_BASE_API_URL,
    S3_ENDPOINT,
  ]
    .filter(Boolean)
    .map((url) => new URL(url, window.location.origin).origin),
);

const isTrustedRequest = (config: { baseURL?: string; url?: string }) => {
  try {
    const base = new URL(config.baseURL || "", window.location.origin);
    return TRUSTED_ORIGINS.has(new URL(config.url || "", base).origin);
  } catch {
    return false;
  }
};

const requestInterceptorOnFullfilled = async (config: any) => {
  if (!isTrustedRequest(config)) return config;

  // Clerk session tokens are short-lived; getToken returns a cached one or refreshes it.
  const token = await getSessionToken();

  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  return config;
};

const requestInterceptorOnRejected = (error: any) => {
  Promise.reject(error);
};

axiosInstance.interceptors.request.use(requestInterceptorOnFullfilled, requestInterceptorOnRejected);
supplierContentAxiosInstance.interceptors.request.use(requestInterceptorOnFullfilled, requestInterceptorOnRejected);

// Response interceptor
const responseInterceptorOnFullfilled = (response: any) => {
  return response;
};
const responseInterceptorOnRejected = (error: any) => {
  const errorResponse = error.response;

  // no token, invalid token, or the Clerk user is not a Hub user
  if (errorResponse?.status === 401 && AUTH_ERROR_MESSAGES.includes(errorResponse.data?.message)) {
    logoutOnce();
    return;
  }

  return Promise.reject(error);
};

axiosInstance.interceptors.response.use(responseInterceptorOnFullfilled, responseInterceptorOnRejected);
supplierContentAxiosInstance.interceptors.response.use(responseInterceptorOnFullfilled, responseInterceptorOnRejected);

export default axiosInstance;
